import {ViralRadarSchema, type ViralRadarDto} from '@devmil/contracts';

/**
 * O radar ILUSTRATIVO, para o modo fixture.
 *
 * **Nenhum nome de perfil real aparece aqui, e essa é a cerca principal deste
 * arquivo.** Fixture com dado que parece verdadeiro vira peça publicada no dia
 * em que alguém copiar dela, e aqui o dado que pareceria verdadeiro seria o
 * nome de uma pessoa: um `@` plausível numa tela de radar convida ao repost
 * que este plano inteiro recusou. Por isso todo usuário termina em palavra que
 * se declara exemplo (`.ilustrativo`, `.exemplo`, `.demonstracao`,
 * `.amostra`), e existe teste que cobra esse sufixo de qualquer linha nova.
 *
 * As quatro linhas cobrem os quatro casos que a tela precisa saber desenhar, e
 * não são quatro variações da mesma coisa:
 *
 * 1. topo do dia, com duas capturas e pauta já criada pela regra automática;
 * 2. segunda colocada, também já virada em pauta pela mesma regra;
 * 3. miniatura ausente, primeira leitura (sem variação) e sem visualização;
 * 4. sem leitura de modelo, que é o estado padrão enquanto o interruptor da
 *    OpenAI estiver desligado.
 *
 * O endereço da miniatura aponta para `exemplo.invalid` de propósito: nenhum
 * byte de mídia de terceiro entra neste repositório, nem como arquivo nem como
 * dado embutido. Na revisão visual o endereço não resolve e a tela cai no
 * quadro neutro, que é justamente o estado previsto para quando a URL da Meta
 * vence.
 */
export const ILLUSTRATIVE_VIRAL_RADAR: ViralRadarDto =
  // Validar a fixture no mesmo schema da API: fixture que não passa no
  // contrato é fixture que mente sobre o formato do dado real.
  ViralRadarSchema.parse({
    availability: 'available',
    illustrative: true,
    // O mesmo dia do resto do painel ilustrativo (`today.date`), senão o radar
    // falaria de uma data que nenhuma outra tela da fixture conhece.
    localDate: '2026-08-27',
    /*
     * O RECIBO da rodada, e ele não é enfeite da fixture.
     *
     * Até 06/09/2026 a página decidia disponibilidade lendo a configuração, e
     * dizia "o radar rodou" sem nenhuma rodada ter acontecido (achado F14). A
     * fixture tem recibo porque a tela agora tira o estado dele: uma fixture
     * sem recibo mostraria a página num estado que o dado real nunca produz.
     */
    lastRun: {
      startedAt: '2026-08-27T19:40:00Z',
      finishedAt: '2026-08-27T19:40:42Z',
      outcome: 'sucesso',
      reason: null,
      graphCode: null,
      applied: true,
      trigger: 'agendador',
      profilesRequested: 6,
      profilesRead: 6,
      profilesRefused: 0,
      mediaEligible: 4,
      mediaNew: 3,
      mediaUpdated: 1,
    },
    setup: {
      perfisConfigurados: true,
      horariosConfigurados: true,
      curadoriaConfigurada: true,
    },
    /*
     * Uma referência CURADA, para a tela saber desenhar o caminho viável.
     *
     * Ela existe na fixture pelo mesmo motivo das quatro linhas descobertas:
     * é um estado que a página precisa saber desenhar, e ele é hoje o único
     * caminho que produz pauta enquanto a conta não lê perfil de terceiro.
     *
     * O endereço aponta para `exemplo.invalid`, como o resto: nenhum endereço
     * real de terceiro entra nesta fixture, e nenhum byte de mídia alheia entra
     * neste repositório.
     */
    curated: [
      {
        id: 'b41c8f7a-9d25-4e63-8b10-5c7a2e94d306',
        creditTo: 'Instituto Ilustrativo de Automação',
        permalink: 'https://exemplo.invalid/estudo-ilustrativo-automacao',
        mediaType: 'CAROUSEL_ALBUM',
        publishedAt: '2026-08-24T09:00:00Z',
        curatedAt: '2026-08-26T11:15:00Z',
        curatedBy: 'curadoria.ilustrativa',
        note: 'Estudo ILUSTRATIVO desta fixture: exemplo de fonte oficial que sustenta um carrossel sobre automação em empresa pequena.',
        rights: 'fonte_oficial',
        topicId: null,
      },
    ],
    posts: [
      {
        id: '9e4f0a65-2b73-4c18-8a52-7f3d6c1e0b94',
        profileUsername: 'consultoria.amostra',
        mediaType: 'CAROUSEL_ALBUM',
        permalink: 'https://exemplo.invalid/p/exemplo-ilustrativo-1/',
        thumbnailUrl: 'https://exemplo.invalid/miniatura-ilustrativa-1.jpg',
        caption:
          'Legenda ILUSTRATIVA, escrita para esta fixture: exemplo de carrossel sobre automação de tarefa repetitiva em empresa pequena.',
        postedAt: '2026-08-26T13:20:00Z',
        score: 0.81,
        scoreBreakdown: {
          engajamento: 0.88,
          recencia: 0.82,
          tema: 0.74,
          formato: 0.8,
        },
        themeTerms: ['automação', 'pequena empresa'],
        metrics: {
          likeCount: 12480,
          commentCount: 318,
          viewCount: 84200,
          followerCount: 41200,
          capturedAt: '2026-08-27T19:40:00Z',
          deltaLikes: 4130,
          deltaComments: 96,
        },
        reading: {
          hook: 'A conta do antes e do depois aparece no primeiro quadro',
          whyItWorked:
            'Leitura ILUSTRATIVA: o número entra antes da explicação, e a promessa cabe na primeira linha da legenda.',
          format: 'Carrossel de 6 quadros, um número por quadro',
        },
        // As duas primeiras já viraram pauta pela regra automática do topo do
        // dia, que é o padrão do plano. O botão some nelas, e a linha diz por
        // quê: criar a segunda pauta da mesma tendência é o caminho para
        // publicar duas peças sobre a mesma coisa.
        topicId: 'b28d4c17-6f93-4a20-9e75-1c8b0d3f5a62',
        illustrative: true,
      },
      {
        id: '3f9a2b71-8c4d-4e15-9a63-2d7b0c5e8140',
        profileUsername: 'perfil.ilustrativo',
        mediaType: 'REELS',
        permalink: 'https://exemplo.invalid/reel/exemplo-ilustrativo-2/',
        thumbnailUrl: 'https://exemplo.invalid/miniatura-ilustrativa-2.jpg',
        caption:
          'Legenda ILUSTRATIVA, escrita para esta fixture: exemplo de vídeo curto sobre agente que responde orçamento fora do horário.',
        postedAt: '2026-08-26T21:05:00Z',
        score: 0.74,
        scoreBreakdown: {
          engajamento: 0.79,
          recencia: 0.91,
          tema: 0.58,
          formato: 1,
        },
        themeTerms: ['agente', 'atendimento'],
        metrics: {
          likeCount: 9640,
          commentCount: 205,
          viewCount: 61300,
          followerCount: 28700,
          capturedAt: '2026-08-27T19:40:00Z',
          deltaLikes: 2810,
          deltaComments: 61,
        },
        reading: {
          hook: 'Uma pergunta de cliente respondida às 23h',
          whyItWorked:
            'Leitura ILUSTRATIVA: mostra a cena inteira em 20 segundos e deixa o resultado no último quadro.',
          format: 'Vídeo vertical, fala direta na câmera',
        },
        topicId: 'd41a7b60-3c85-4f92-b7e1-0a5d9c2f4e83',
        illustrative: true,
      },
      {
        id: '5b1c8d42-7e30-4a96-8f24-1c9e3b7d0a58',
        profileUsername: 'oficina.exemplo',
        mediaType: 'VIDEO',
        permalink: 'https://exemplo.invalid/reel/exemplo-ilustrativo-3/',
        // Miniatura ausente: o endereço da Meta VENCE, e a linha continua
        // utilizável sem ele. É o caso que a tela desenha como quadro neutro.
        thumbnailUrl: null,
        caption:
          'Legenda ILUSTRATIVA, escrita para esta fixture: exemplo de vídeo sobre planilha que virou rotina automática.',
        postedAt: '2026-08-27T11:50:00Z',
        score: 0.66,
        scoreBreakdown: {
          engajamento: 0.61,
          recencia: 0.98,
          tema: 0.52,
          formato: 0.9,
        },
        themeTerms: ['planilha', 'rotina'],
        metrics: {
          likeCount: 3170,
          commentCount: 142,
          // Nem todo tipo de mídia devolve visualização, e zero é uma medição
          // que este campo não tem direito de afirmar.
          viewCount: null,
          followerCount: 9800,
          capturedAt: '2026-08-27T19:40:00Z',
          // Uma captura só: "medi duas vezes e não mudou" é notícia, "só medi
          // uma vez" não é, e as duas não podem sair iguais na tela.
          deltaLikes: null,
          deltaComments: null,
        },
        reading: {
          hook: 'A planilha preenchida à mão, e a mesma planilha rodando só',
          whyItWorked:
            'Leitura ILUSTRATIVA: o antes e o depois cabem no mesmo enquadramento, sem corte no meio.',
          format: 'Vídeo de tela, sem rosto',
        },
        topicId: null,
        illustrative: true,
      },
      {
        id: '7c2d9e53-4a61-4b07-9d38-6e0f1a2b3c49',
        profileUsername: 'estudio.demonstracao',
        mediaType: 'IMAGE',
        permalink: 'https://exemplo.invalid/p/exemplo-ilustrativo-4/',
        thumbnailUrl: 'https://exemplo.invalid/miniatura-ilustrativa-4.jpg',
        caption:
          'Legenda ILUSTRATIVA, escrita para esta fixture: exemplo de foto com um número grande e uma frase curta.',
        postedAt: '2026-08-25T16:30:00Z',
        score: 0.58,
        scoreBreakdown: {
          engajamento: 0.66,
          recencia: 0.44,
          tema: 0.69,
          formato: 0.5,
        },
        themeTerms: ['lucro'],
        metrics: {
          likeCount: 5210,
          commentCount: 87,
          viewCount: null,
          followerCount: 15400,
          capturedAt: '2026-08-27T19:40:00Z',
          deltaLikes: 890,
          deltaComments: 12,
        },
        // Sem leitura, que é o estado PADRÃO: a leitura por modelo está atrás
        // de interruptor próprio e nasce desligada. A linha vale mesmo assim,
        // porque a pontuação e os números vieram da plataforma, e uma leitura
        // inventada seria pior que leitura nenhuma.
        reading: null,
        topicId: null,
        illustrative: true,
      },
    ],
  });
